import { createTheme } from '@mui/material/styles'

export const scTheme = {
  asap: "Asap, -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Noto Sans', Roboto, 'Helvetica Neue', sans-serif;",
  gotham: "'Gotham Rounded A', 'Gotham Rounded B', Asap, -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Noto Sans', Roboto, 'Helvetica Neue', sans-serif",
  gothamSmall: "'Gotham Rounded SSm A', 'Gotham Rounded SSm B', Asap, -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Noto Sans', Roboto, 'Helvetica Neue', sans-serif",
  cardShadow: '2px 3px 3px 0 rgba(0, 0, 0, 0.16)',
  buttonShadow: '2px 4px 6px 0 rgba(0, 0, 0, 0.3)',
  blue: '#009bde',
  orange: '#ff8200',
  gray: '#58595b',
  red: '#df3603',
  light: '#f6f6f6',
  nearWhite: '#f9f9f9',
  white: '#ffffff'
}

const theme = createTheme({
  palette: {
    primary: {
      main: '#FF8200',
      dark: '#EA6C01'
    },
    secondary: {
      main: '#009BDE',
      dark: '#005980'
    },
    info: {
      main: '#005980'
    },
    success: {
      main: '#95D600'
    },
    error: {
      main: '#DF3603'
    },
    text: {
      primary: '#313233',
      secondary: '#005980'
    },
    background: {
      default: '#FFFFFF'
    },
    contrastThreshold: 2
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 1024,
      lg: 1280,
      xl: 1600
    }
  }
})

export const myloTheme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 1024,
      lg: 1280,
      xl: 1600
    }
  },
  palette: {
    primary: {
      main: theme.palette.primary.main
    },
    secondary: {
      main: theme.palette.secondary.main
    },
    info: {
      main: theme.palette.info.main
    },
    success: {
      main: theme.palette.success.main
    },
    error: {
      main: theme.palette.error.main
    },
    text: {
      primary: theme.palette.text.primary,
      secondary: theme.palette.text.secondary
    },
    background: {
      default: theme.palette.background.default
    },
    contrastThreshold: 2
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          padding: theme.spacing(2)
        },
        rounded: {
          borderRadius: '20px',
          padding: theme.spacing(2)
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: '#4E4F52',
          boxShadow: 'none',
          borderRadius: 2,
          '&:hover': {
            color: theme.palette.secondary.main,
            backgroundColor: 'inherit'
          },
          '&:focus': {
            backgroundColor: 'inherit'
          },
          '&.Mui-focusVisible': {
            color: theme.palette.secondary.main,
            boxShadow: `0 0 0 2px ${theme.palette.text.secondary}`,
            backgroundColor: 'inherit'
          }
        },
        colorPrimary: {
          '&:hover': {
            backgroundColor: 'inherit'
          }
        }
      }
    },
    MuiRadio: {
      defaultProps: {
        color: 'secondary'
      },
      styleOverrides: {
        root: {
          color: '#747474'
        }
      }
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          boxSizing: 'content-box'
        },
        colorPrimary: {
          '&:hover': {
            color: theme.palette.primary.dark
          }
        },
        colorSecondary: {
          '&:hover': {
            color: theme.palette.secondary.dark
          }
        }
      }
    },
    MuiLink: {
      styleOverrides: {
        root: {
          borderRadius: 2,
          '&:focus': {
            outline: 'none'
          },
          '&.Mui-focusVisible': {
            color: theme.palette.secondary.main,
            boxShadow: `0 0 0 2px ${theme.palette.text.secondary}`
          }
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          padding: theme.spacing(2, 2, 2, 2),
          minWidth: theme.spacing(28),
          fontSize: '1.25rem',
          borderRadius: '100px',
          boxShadow: 'none',
          '&:focus': {
            backgroundColor: 'inherit'
          },
          '&:hover': {
            backgroundColor: 'inherit'
          }
        },
        sizeSmall: {
          padding: theme.spacing(1.5, 1.5, 1.5, 1.5),
          minWidth: theme.spacing(21),
          fontSize: '1rem'
        },
        sizeLarge: {
          padding: theme.spacing(2, 2, 2, 2),
          minWidth: theme.spacing(40),
          fontSize: '1.25rem'
        },
        text: {
          padding: theme.spacing(1),
          width: 'auto',
          fontSize: '0.75rem',
          fontWeight: theme.typography.fontWeightMedium,
          color: '#4E4F52',
          '&:hover': {
            color: theme.palette.secondary.main,
            backgroundColor: 'unset'
          },
          transition: 'none',
          borderRadius: 2,
          '&.Mui-focusVisible': {
            color: theme.palette.secondary.main,
            boxShadow: `0 0 0 2px ${theme.palette.text.secondary}`
          }
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none'
          },
          '&:focus': {
            boxShadow: 'none'
          },
          '&:disabled': {
            color: theme.palette.common.white,
            backgroundColor: '#B3B3B3'
          }
        },
        containedPrimary: {
          '&:hover': {
            backgroundColor: theme.palette.primary.dark
          },
          '&:focus': {
            backgroundColor: theme.palette.primary.light
          }
        },
        containedSecondary: {
          '&:hover': {
            backgroundColor: theme.palette.secondary.dark
          },
          '&:focus': {
            backgroundColor: theme.palette.secondary.light
          }
        },
        outlined: {
          padding: '13px 24px 13px 24px',
          '&:disabled': {
            color: '#B3B3B3',
            border: '3px solid #B3B3B3',
            backgroundColor: 'white'
          }
        },
        outlinedPrimary: {
          color: theme.palette.primary.main,
          backgroundColor: 'white',
          border: `3px solid ${theme.palette.primary.main}`,
          '&:hover': {
            backgroundColor: theme.palette.common.white,
            color: theme.palette.primary.dark,
            border: `3px solid ${theme.palette.primary.dark}`
          },
          '&:focus': {
            backgroundColor: theme.palette.common.white,
            color: theme.palette.primary.light,
            border: `3px solid ${theme.palette.primary.light}`
          }
        },
        outlinedSecondary: {
          color: theme.palette.secondary.main,
          backgroundColor: 'white',
          border: `3px solid ${theme.palette.secondary.main}`,
          '&:hover': {
            backgroundColor: theme.palette.common.white,
            color: theme.palette.secondary.main,
            border: `3px solid ${theme.palette.secondary.dark}`
          },
          '&:focus': {
            backgroundColor: theme.palette.common.white,
            color: theme.palette.secondary.main,
            border: `3px solid ${theme.palette.secondary.dark}`
          }
        },
        outlinedSizeLarge: {
          padding: '13px 24px 13px 24px !important'
        },
        outlinedSizeSmall: {
          padding: '9px 24px 9px 24px !important'
        }
      }
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          padding: 6,
          borderRadius: 2
        },
        colorPrimary: {
          color: '#747474',
          '&.Mui-checked': {
            color: theme.palette.secondary.main
          }
        },
        colorSecondary: {
          color: theme.palette.common.white,
          '&.Mui-checked': {
            color: theme.palette.secondary.main
          }
        }
      }
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          '&:hover': {
            color: theme.palette.secondary.main
          }
        }
      }
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          color: theme.palette.text.secondary
        }
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '&:hover': {
            '& label': {
              color: theme.palette.secondary.main
            },
            '& label.Mui-disabled': {
              color: 'rgba(0, 0, 0, 0.38)'
            }
          }
        }
      }
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          '&.Mui-focused': {
            color: theme.palette.secondary.main
          }
        }
      }
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          backgroundColor: '#F5F5F5',
          borderTopRightRadius: theme.spacing(1),
          borderTopLeftRadius: theme.spacing(1),
          borderBottomRightRadius: theme.spacing(1),
          borderBottomLeftRadius: theme.spacing(1),
          '&:hover': {
            backgroundColor: '#F5F5F5'
          },
          '&.Mui-focused': {
            backgroundColor: '#F5F5F5'
          },
          '&.Mui-disabled': {
            backgroundColor: '#F5F5F5'
          }
        },
        colorSecondary: {
          backgroundColor: theme.palette.common.white,
          '&:hover': {
            backgroundColor: theme.palette.common.white
          },
          '&.Mui-focused': {
            backgroundColor: theme.palette.common.white
          },
          '&.Mui-disabled': {
            backgroundColor: theme.palette.common.white
          }
        }
      }
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          '&:focus': {
            backgroundColor: 'none',
            borderTopRightRadius: theme.spacing(1),
            borderTopLeftRadius: theme.spacing(1),
            borderBottomRightRadius: theme.spacing(1),
            borderBottomLeftRadius: theme.spacing(1)
          }
        }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: '#616161',
          fontWeight: theme.typography.fontWeightLight
        }
      }
    }
  },
  typography: {
    fontFamily: [
      'Gotham Rounded A',
      'Gotham Rounded B',
      'Gotham Rounded SSm A',
      'Gotham Rounded SSm B',
      'Asap',
      '-apple-system',
      'BlinkMacSystemFont'
    ].join(','),
    h1: {
      fontSize: '3.75rem',
      [theme.breakpoints.down('xs')]: {
        fontSize: '2rem'
      }
    },
    h2: {
      fontSize: '3rem',
      [theme.breakpoints.down('xs')]: {
        fontSize: '2rem'
      }
    },
    h3: {
      fontSize: '2.625rem',
      [theme.breakpoints.down('xs')]: {
        fontSize: '2rem'
      }
    },
    h4: {
      [theme.breakpoints.down('xs')]: {
        fontSize: '2rem'
      }
    },
    h5: {
      fontFamily: 'Gotham Rounded SSm A, Gotham Rounded SSm B',
      [theme.breakpoints.down('xs')]: {
        fontSize: '1.25rem'
      }
    },
    h6: {
      fontFamily: 'Gotham Rounded SSm A, Gotham Rounded SSm B',
      lineHeight: 1.2
    },
    subtitle1: {
      fontFamily: 'Gotham Rounded SSm A, Gotham Rounded SSm B'
    },
    subtitle2: {
      fontFamily: 'Gotham Rounded SSm A, Gotham Rounded SSm B'
    },
    body1: {
      fontFamily: 'Gotham Rounded SSm A, Gotham Rounded SSm B'
    },
    caption: {
      fontFamily: 'Gotham Rounded SSm A, Gotham Rounded SSm B'
    },
    body2: {
      fontFamily: 'Gotham Rounded SSm A, Gotham Rounded SSm B'
    },
    button: {
      fontSize: '1.25rem',
      lineHeight: '1rem',
      fontWeight: 500,
      textTransform: 'none',
      fontFamily: 'Gotham Rounded SSm A, Gotham Rounded SSm B'
    }
  }
})
